import type { Records } from '@roundstead/shared/library';
import type { Action } from '@roundstead/shared/ledger';
import type { Screen } from '@roundstead/shared/replies';

export interface TurnResult { reply: string; next: string; fallback: boolean; records: Records }

/** The service can't be reached (the Mac or the tunnel is down) or the facilitator took it down: "Roundstead is unavailable". */
export class Unavailable extends Error {}

declare const __RS_API__: string;
/** Where the service is. Empty when the Mac serves the app itself; https://api.roundstead.club when the app is on Cloudflare Pages. */
export const API_BASE: string = typeof __RS_API__ === 'string' ? __RS_API__ : '';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let r: Response;
  try { r = await fetch(API_BASE + path, { ...init, headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) } }); }
  catch { throw new Unavailable('unreachable'); }
  if ([502, 503, 504, 521, 522, 523, 524, 530].includes(r.status)) throw new Unavailable(`unavailable (${r.status})`);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error((body as { error?: string }).error ?? `request failed (${r.status})`), { code: (body as { code?: string }).code });
  return body as T;
}

/** The ledger refused an action; code says why (stale_version, attempt_open, unpriced, not_served...). Nothing changed. */
export class Refused extends Error { code: string; constructor(code: string, message: string) { super(message); this.code = code; } }

export const api = (code: string) => {
  const at = (path: string) => `/api/s/${encodeURIComponent(code)}${path}`;
  return {
    records: () => call<Records>(at('')),
    photo: (name: string, data: Blob) => call<{ id: string; name: string; state: 'received' }>(at(`/photos?name=${encodeURIComponent(name.slice(0, 80))}`), { method: 'POST', body: data, headers: { 'content-type': 'image/jpeg' } }),
    removePhoto: (id: string) => call<Records>(at(`/photos/${encodeURIComponent(id)}`), { method: 'DELETE' }),
    progress: (view: number, page: string | null) => call<{ ok: true }>(at('/progress'), { method: 'POST', body: JSON.stringify({ view, page }) }),
    events: (events: unknown[]) => call<{ ok: true }>(at('/events'), { method: 'POST', body: JSON.stringify({ events }), keepalive: true }),
    turn: (msg: { text?: string; photoIds?: string[]; screen?: Screen }) => call<TurnResult>(at('/turn'), { method: 'POST', body: JSON.stringify(msg) }),
    /** A household action on the ledger. Throws Refused when the current state doesn't permit it. */
    act: async (action: Action) => {
      try { return await call<Records>(at('/act'), { method: 'POST', body: JSON.stringify(action) }); }
      catch (e) { const c = (e as { code?: string }).code; if (c) throw new Refused(c, (e as Error).message); throw e; }
    },
  };
};

/** Re-encodes a photo through a canvas: shrinks it to 1600px and drops all metadata, including location. */
export async function preparePhoto(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close();
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('could not read photo'))), 'image/jpeg', 0.8));
}
